if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      // Inventory state
      variantInventoryMap = null;

      // Read-only URL watcher state
      _urlPoll = null;
      _lastUrlVariantId = null;

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        // Keep the theme pub/sub flow
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // Start the minimal, safe URL watcher (no event interception)
        this.startVariantUrlWatcher();

        // Prime inventory map & set initial UI (also covered by the watcher if ?variant= is present)
        this.variantInventoryMap = this.readInventoryMapFromDom(document) || this.variantInventoryMap;
        this.updateInventoryUI(this.getCurrentVariantFromDom());

        this.checkInitialVariantState();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.stopVariantUrlWatcher?.();
      }

      /* ===================== READ-ONLY URL WATCHER ====================== */

      startVariantUrlWatcher() {
        const readVariantFromUrl = () => new URL(window.location.href).searchParams.get('variant');

        // Initial sync
        this._lastUrlVariantId = readVariantFromUrl();
        if (this._lastUrlVariantId) {
          this.updateInventoryUI(this.getVariantById(this._lastUrlVariantId));
        } else {
          // fallback to DOM-derived variant when no ?variant in URL
          this.updateInventoryUI(this.getCurrentVariantFromDom());
        }

        // Lightweight poll; does not touch history or events
        this._urlPoll = setInterval(() => {
          const id = readVariantFromUrl();
          if (id !== this._lastUrlVariantId) {
            this._lastUrlVariantId = id;
            this.updateInventoryUI(this.getVariantById(id));
          }
        }, 200);
      }

      stopVariantUrlWatcher() {
        if (this._urlPoll) {
          clearInterval(this._urlPoll);
          this._urlPoll = null;
        }
      }

      /* ======================== INVENTORY HELPERS ======================== */

      readInventoryMapFromDom(root = document) {
        try {
          const byId = root.getElementById?.(`VariantInventory-${this.sectionId}`);
          if (byId?.textContent?.trim()) return JSON.parse(byId.textContent.trim());
        } catch (e) {
          console.warn('VariantInventory JSON (by id) parse failed', e);
        }
        try {
          const byAttr = root.querySelector?.('script[data-variant-inventory]');
          if (byAttr?.textContent?.trim()) return JSON.parse(byAttr.textContent.trim());
        } catch (e) {
          console.warn('VariantInventory JSON (by attr) parse failed', e);
        }
        if (typeof window !== 'undefined' && window.variantStock && typeof window.variantStock === 'object') {
          return window.variantStock;
        }
        return null;
      }

      updateInventoryUI(variant, html) {
        // Find the inventory block for this section (be tolerant)
        const inventoryEl =
          this.querySelector?.(`#Inventory-${this.dataset.section}`) ||
          this.querySelector?.('[id^="Inventory-"]');
        if (!inventoryEl) return;

        inventoryEl.classList.remove('hidden');

        // If we have fresh HTML from a section render, mirror it first
        if (html) {
          const src = html.getElementById?.(`Inventory-${this.sectionId}`) || html.querySelector?.('[id^="Inventory-"]');
          if (src) inventoryEl.innerHTML = src.innerHTML;
        }

        const textNode =
          inventoryEl.querySelector('[data-inventory-text]') ||
          inventoryEl.querySelector('span:not(.svg-wrapper)') ||
          inventoryEl;

        // Refresh quantity sources
        this.variantInventoryMap = this.readInventoryMapFromDom(document) || this.variantInventoryMap;

        let qty = null;
        if (this.variantInventoryMap && variant?.id in this.variantInventoryMap) {
          qty = Number(this.variantInventoryMap[variant.id]);
        }
        if (qty == null && variant && typeof variant.inventory_quantity !== 'undefined') {
          qty = Number(variant.inventory_quantity);
        }

        if (!variant || variant.available === false || (qty != null && qty <= 0)) {
          textNode.textContent = 'Niet op voorraad';
          return;
        }
        if (qty == null) {
          textNode.textContent = 'Op voorraad';
          return;
        }
        if (qty === 1) {
          textNode.textContent = 'Er is 1 stuk op voorraad';
        } else if (qty > 10) {
          textNode.textContent = 'Er zijn meer dan 10 stuks op voorraad';
        } else {
          textNode.textContent = `Er zijn ${qty} stuks op voorraad`;
        }
      }

      /* ==================== VARIANT RESOLUTION HELPERS ==================== */

      getVariantById(id) {
        if (!id) return null;
        const product = this.readProductJson();
        if (product?.variants?.length) {
          return product.variants.find((v) => String(v.id) === String(id)) || { id: Number(id), available: true };
        }
        return { id: Number(id), available: true };
      }

      getCurrentVariantFromDom() {
        // 1) Theme-provided selected variant JSON (if present)
        const selectedFromTheme = this.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (selectedFromTheme) {
          try {
            return JSON.parse(selectedFromTheme);
          } catch (_) {}
        }

        // 2) URL ?variant=...
        const idFromUrl = new URL(window.location.href).searchParams.get('variant');
        if (idFromUrl) return this.getVariantById(idFromUrl);

        // 3) Fallback to the hidden input id within/for this section
        const id =
          this.querySelector(`#product-form-${this.dataset.section} input[name="id"]`)?.value ||
          this.querySelector('input[name="id"]')?.value;
        if (id) return this.getVariantById(id);

        return null;
      }

      readProductJson() {
        try {
          const byId = document.getElementById(`ProductJson-${this.sectionId}`);
          if (byId?.textContent?.trim()) return JSON.parse(byId.textContent);
        } catch (_) {}
        try {
          const byAttr = this.querySelector('script[type="application/json"][data-product]');
          if (byAttr?.textContent?.trim()) return JSON.parse(byAttr.textContent);
        } catch (_) {}
        return null;
      }

      /* ======================== ORIGINAL THEME LOGIC ===================== */

      checkInitialVariantState() {
        setTimeout(() => {
          const submitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
          if (submitButton && submitButton.hasAttribute('disabled')) {
            submitButton.classList.add('disabled');
          }
          const paymentButton = this.querySelector('.shopify-payment-button__button');
          if (paymentButton && paymentButton.getAttribute('aria-disabled') === 'true') {
            paymentButton.setAttribute('disabled', '');
            paymentButton.disabled = true;
          }
        }, 500);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          // After swap, refresh inventory & UI
          this.variantInventoryMap = this.readInventoryMapFromDom(html) || this.variantInventoryMap;
          this.updateInventoryUI(variant, html);
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((r) => r.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selected = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selected ? JSON.parse(selected) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // Update voorraadtekst immediately
          this.variantInventoryMap = this.readInventoryMapFromDom(html) || this.variantInventoryMap;
          this.updateInventoryUI(variant, html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        if (this.querySelector('media-gallery').getAttribute('has_variant') === 'true') {
          const source = this.querySelector('media-gallery');
          const dest = html.querySelector(`media-gallery`);
          source.outerHTML = dest.outerHTML;
          return;
        }

        const sourceList = this.querySelector('media-gallery ul');
        const destList = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const items = Array.from(sourceList.querySelectorAll('li[data-media-id]'));
          const set = new Set(items.map((i) => i.dataset.mediaId));
          const map = new Map(items.map((i, idx) => [i.dataset.mediaId, { item: i, index: idx }]));
          return [items, set, map];
        };

        if (sourceList && destList) {
          let [items, set, map] = refreshSourceData();
          const destItems = Array.from(destList.querySelectorAll('li[data-media-id]'));
          const destSet = new Set(destItems.map(({ dataset }) => dataset.mediaId));
          let changed = false;

          // add items from new data not present in DOM
          for (let i = destItems.length - 1; i >= 0; i--) {
            if (!set.has(destItems[i].dataset.mediaId)) {
              sourceList.prepend(destItems[i]);
              changed = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < items.length; i++) {
            if (!destSet.has(items[i].dataset.mediaId)) {
              items[i].remove();
              changed = true;
            }
          }

          if (changed) [items, set, map] = refreshSourceData();

          const destItems2 = Array.from(destList.querySelectorAll('li[data-media-id]'));
          destItems2.forEach((dItem, dIdx) => {
            const sData = map.get(dItem.dataset.mediaId);
            if (sData && sData.index !== dIdx) {
              sourceList.insertBefore(sData.item, sourceList.querySelector(`li:nth-of-type(${dIdx + 1})`));
              [items, set, map] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((r) => r.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

/* ---------------------------------------------------------------------------
OPTIONAL: expose exact per-variant quantities once in your product section.

<script type="application/json" id="VariantInventory-{{ section.id }}">
  {
    {% for v in product.variants %}
      "{{ v.id }}": {{ v.inventory_quantity | default: 0 }}{% unless forloop.last %},{% endunless %}
    {% endfor %}
  }
</script>
--------------------------------------------------------------------------- */